/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent" />
import * as Contracts from "DistributedTask/ConnectedService/Client/Contracts/ConnectedService";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorStore, IEndpointsEditorState } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import * as EndpointOAuthHelper from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/EndpointOAuthHelper";
import { cloneEndpoint, EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { IGenericAuthorizationOAuthProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/OAuthHelper";
import { VisibilityHelper } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/VisibilityHelper";
import * as React from "react";
import { ServiceEndpointTypes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { InputDataType, InputDescriptor, InputMode, InputValidation, InputValue, InputValues } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { DependencyBinding, ServiceEndpoint, EndpointAuthorization, ServiceEndpointAuthenticationScheme, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Dialog } from "VSSUI/Dialog";
import { FileInput, FileInputResult, FileInputUpdateEventData } from "VSSUI/FileInput";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface IGenericAuthorizationComponentProps extends IEndpointEditorComponentProps {
    oAuthProps?: IGenericAuthorizationOAuthProps;
    onValidationChange?: (descriptor: InputDescriptor, isValid: boolean, isPresent: boolean) => void;
    renderAuthenticationHeader?: () => JSX.Element;
}

export interface IGenericAuthorizationComponentState extends IEndpointsEditorState {
    showUploadDialog: boolean;
    fileContent: FileInputResult;
}

export class GenericAuthorizationComponent extends VssComponent<IGenericAuthorizationComponentProps, IGenericAuthorizationComponentState, {}> {
    constructor(props: IGenericAuthorizationComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._draftServiceEndpoint = this._copyDraft(this.props.draftEndpoint);

        this._draftAuth = this._draftServiceEndpoint.authorization;
        this._selectedAuthScheme = this._draftAuth.scheme;

        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);

        this._isDirtyDictionary = {};
        this._initializeInputErrors();
        this._setInputDescriptorWithUrlDependency();
        if (this.shouldShowUrl(props.serviceEndpointType) && !!this._inputDescriptorIdWithUrlDependency) {
            this._updateInputsErrors(this._draftServiceEndpoint.url,
                this._urlInputValidation, this._endpointUrlKey, false);
        }
        this.configKey = new DropdownSelection();
        this.state = {
            showUploadDialog: false,
            fileContent: null,
            serviceEndpointType: null,
            serviceEndpoint: null,
            openToAllPipelinesFlag: null,
            isError: null,
            isDraft: null,
            errorMessage: null,
            configurations: []
        };

        this._initializeConfigurationsIfRequired(this.props.serviceEndpointType, false);
        this._initializeDependencyMap();
        this._isNewOAuthUx = !this.props.oAuthProps ? false : this.props.oAuthProps.oAuthHelper.isNewOAuthUx(this._draftAuth.scheme);
    }

    public componentDidMount() {
        this.setState(this._endpointEditorStore.getState());
        this._endpointEditorStore.addChangedListener(this._onEndpointEditorStoreChange);
    }

    public componentWillUnmount() {
        this._endpointEditorStore.removeChangedListener(this._onEndpointEditorStoreChange);
    }

    // Not using componentDidUpdate here because the parent draftEndpoint doesn't get updated correctly
    // It leads to problems with auth picker radio options
    public UNSAFE_componentWillReceiveProps(props: IGenericAuthorizationComponentProps) {
        this._draftServiceEndpoint = this._copyDraft(props.draftEndpoint);
        this._draftAuth = this._draftServiceEndpoint.authorization;

        let hasAuthSchemeChanged = this._selectedAuthScheme !== props.draftEndpoint.authorization.scheme;

        this._selectedAuthScheme = this._draftAuth.scheme;
        if ((this.props.isUpdateForm && !this._isErrorDictInitialized) || hasAuthSchemeChanged) {
            this._initializeInputErrors();
            if (this.shouldShowUrl(this.props.serviceEndpointType)) {
                this._updateInputsErrors(this._draftServiceEndpoint.url, this._urlInputValidation, this._endpointUrlKey, false);
            }

            this._initializeConfigurationsIfRequired(this.props.serviceEndpointType, hasAuthSchemeChanged);
            this._isErrorDictInitialized = true;
        }
    }

    public render(): React.ReactNode {
        if (this._isNewOAuthUx) {
            return this.oAuthRender();
        }
        return (
            <div className="auth-scheme-details-section">
                {!this._inputDescriptorIdWithUrlDependency && this._getUrlComponent()}
                {
                    this.props.serviceEndpointType && this.props.serviceEndpointType.inputDescriptors && this._getEndpointDetailsInputs()
                }
                {
                    this.props.serviceEndpointType.name === Resources.SSHEndpointType &&
                    <span><Link className="file-input-container" onClick={this.onUploadClick} >{Resources.SSHUploadPrivateKey}</Link></span>
                }
                {
                    this._getEndpointAuthenticationInputs()
                }
                {
                    this._draftAuth.scheme === Constants.OAuth2Scheme
                        && this._getEndpointAuthenticationOAuth2Input()
                }
                {
                    this.uploadDialog()
                }
            </div>
        );
    }

    private _copyDraft = (draftEndpoint: ServiceEndpoint): ServiceEndpoint => {
        let clone: ServiceEndpoint = cloneEndpoint(draftEndpoint);
        clone.serviceEndpointProjectReferences = draftEndpoint.serviceEndpointProjectReferences;  // by ref
        return clone;
    }

    private _getUrlComponent = (): React.ReactFragment => {
        let isDisabled: boolean = false;
        // When URL is dependent on any other inputDescriptor, we are inheriting the update behavior of that inputDescriptor for URL in update flow.
        if (this._inputDescriptorIdWithUrlDependency && this._inputDescriptorsDictionary[this._inputDescriptorIdWithUrlDependency] && this._inputDescriptorsDictionary[this._inputDescriptorIdWithUrlDependency].properties) {
            isDisabled = this.props.isUpdateForm && this._inputDescriptorsDictionary[this._inputDescriptorIdWithUrlDependency].properties[Constants.updatesNotAllowed];
        }
        return (<div className="auth-scheme-details-url">
                    {
                        this.props.serviceEndpointType &&
                        this.shouldShowUrl(this.props.serviceEndpointType) &&
                        this._showConfigurationDropdown &&
                        <FormItem
                            className="create-service-endpoint-url-text-box default-input-margin-top"
                            error={!!this._getErrorMessage(this._endpointUrlKey)}
                            label={this.props.serviceEndpointType.endpointUrl && this.props.serviceEndpointType.endpointUrl.displayName ?
                                this.props.serviceEndpointType.endpointUrl.displayName : Resources.ServerURLText}
                            message={!this._getErrorMessage(this._endpointUrlKey) ? "" : Resources.InvalidServerURLMessage}
                        >
                            <TextField
                                key={"server-url"}
                                value={this._draftServiceEndpoint.url}
                                onChange={this._onUrlValueChanged}
                                disabled={isDisabled}
                            />
                            <span className="secondary-text fontSizeS">{this.props.serviceEndpointType.endpointUrl && this.props.serviceEndpointType.endpointUrl.helpText}</span>
                        </FormItem>

                    }
                </div>);
    }

    private _onUrlValueChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        this._draftServiceEndpoint.url = value;
        this._isDirtyDictionary[this._endpointUrlKey] = this.props.serviceEndpointType.endpointUrl ? value !== this.props.serviceEndpointType.endpointUrl.value : !!value;
        this._updateInputsErrors(value, this._urlInputValidation, this._endpointUrlKey, false);
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _getEndpointDetailsInputs(): React.ReactFragment {
        return (
            <div className="endpoint-details-input-descriptors">
                {
                    this.props.serviceEndpointType.inputDescriptors.map((inputDescriptor, index) => {
                        let currentKey = Object.keys(this._draftServiceEndpoint.data).find(key => key.toLowerCase() === inputDescriptor.id.toLowerCase());
                        if (!currentKey) {
                            currentKey = inputDescriptor.id;
                        }
                        let currentValue = this._draftServiceEndpoint.data[currentKey];
                        if (currentKey !== inputDescriptor.id) {
                            delete this._draftServiceEndpoint.data[currentKey];
                            this._draftServiceEndpoint.data[inputDescriptor.id] = currentValue;
                        }
                        let drawComponent = true;
                        if (inputDescriptor.properties) {
                            if (inputDescriptor.properties[Constants.VisibleRule]) {
                                drawComponent = VisibilityHelper.getVisibility(inputDescriptor.properties[Constants.VisibleRule], this._getInputValue);
                            }
                        }

                        // removing the invisible components, inputs which are disabled and do not have any value, inputs which don't have any inputMOde and dont have any value from draftEndpoint to prevent errors during validations on Server side
                        if (!drawComponent
                            || (
                                    (
                                        (inputDescriptor.values && inputDescriptor.values.isDisabled
                                             && inputDescriptor.values.defaultValue == null)
                                        || inputDescriptor.inputMode === InputMode.None
                                    ) &&
                                    (
                                        !this._draftServiceEndpoint.data[currentKey]
                                        || this._draftServiceEndpoint.data[currentKey].length === 0
                                    )
                                )
                            ) {
                            this._draftServiceEndpoint.data = this._getUpdatedDraftEndpointDataForInvisibleComponents(inputDescriptor, this._draftServiceEndpoint.data);
                        }

                        if (drawComponent) {
                            let inputKey = this._endpointInputDescriptorsKey + index;
                            let inputComponent = EndpointsEditorPanelUtils.getComponent(inputKey, inputDescriptor, currentValue,
                                (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                                    this._draftServiceEndpoint.data[inputDescriptor.id] = value;
                                    this._inputValuesDictionary[inputDescriptor.id] = value;
                                    this._resetErrorDictionaryForDependentFieldsOfPreviousValue(inputDescriptor.id, currentValue);
                                    this._updateUrlIfApplicable(inputDescriptor.id, value);
                                    this._setDirtyFlag(value, inputDescriptor.values, inputDescriptor.id);
                                    this._updateInputsErrors(value, inputDescriptor.validation, inputDescriptor.id, inputDescriptor.isConfidential);
                                    this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                                    this.setState({});
                                },
                                this._getErrorMessage(inputDescriptor.id),
                                this.props.isUpdateForm,
                                this.context.pageContext,
                                this.configKey);

                            if (currentValue) {
                                this._inputValuesDictionary[inputDescriptor.id] = currentValue;
                            } else {
                                this._inputValuesDictionary[inputDescriptor.id] = EndpointsEditorPanelUtils.getComponentDefaultValue(inputDescriptor, inputComponent);
                            }

                            if (this._errorsDictionary[inputDescriptor.id] && inputDescriptor.values && inputDescriptor.values.isDisabled && inputDescriptor.validation.isRequired) {
                                this._removeInputError(inputDescriptor.id);
                            }

                            if (Utils_String.equals(this._inputDescriptorIdWithUrlDependency, inputDescriptor.id, true)) {
                                return (<React.Fragment key={inputKey}>
                                            <div className="endpoint-details-single-component default-input-margin-top">{inputComponent}</div>
                                            {this._getUrlComponent()}
                                        </React.Fragment>);
                            }
                            else {
                                return <div className="endpoint-details-single-component default-input-margin-top" key={inputKey}>{inputComponent}</div>;
                            }
                        } else {
                            if (this._errorsDictionary[inputDescriptor.id]) {
                                this._removeInputError(inputDescriptor.id);
                            }
                        }

                    })
                }
            </div>
        );
    }

    private _renderAuthenticationHeader(): JSX.Element {
        const renderAuthenticationHeader = this.props.renderAuthenticationHeader;
        if (renderAuthenticationHeader) {
            return renderAuthenticationHeader();
        }
        return <span role="heading" aria-level={4} className="service-endpoint-section-headers">{Resources.AuthenticationHeader}</span>;
    }

    private _getEndpointAuthenticationInputs(): JSX.Element {
        let currentValue = "";

        return (
            <div className="auth-scheme-details-auth-input-descriptors">
                { this._renderAuthenticationHeader() }
                {
                    this.props.serviceEndpointType &&
                    this.props.serviceEndpointType.authenticationSchemes[this._getSelectedAuthSchemeIndex()].inputDescriptors.map((inputDescriptor, index) => {
                        currentValue = this._draftAuth.parameters[inputDescriptor.id];
                        if (this._draftAuth.scheme === Constants.OAuth2Scheme &&
                            this._showConfigurationDropdown) {
                            if (!inputDescriptor.values) {
                                inputDescriptor.values = {
                                    isLimitedToPossibleValues: true
                                } as InputValues;
                            }
                            if (inputDescriptor.id === Constants.ConfigurationId) {
                                inputDescriptor.values.possibleValues = this.state.configurations.map<InputValue>((config) => {
                                    return {
                                        value: config.id,
                                        displayValue: config.name,
                                        data: null
                                    };
                                });
                            }

                        }
                        let drawComponent = true;
                        if (inputDescriptor.properties) {
                            if (inputDescriptor.properties[Constants.VisibleRule]) {
                                drawComponent = VisibilityHelper.getVisibility(inputDescriptor.properties[Constants.VisibleRule], this._getInputValue);
                            }
                        }

                        if (!drawComponent || (((inputDescriptor.values && inputDescriptor.values.isDisabled
                            && inputDescriptor.values.defaultValue == null)
                        || inputDescriptor.inputMode === InputMode.None) && (this._draftAuth.parameters && !this._draftAuth.parameters[inputDescriptor.id] || this._draftAuth.parameters[inputDescriptor.id].length === 0))) {
                            this._draftAuth.parameters = this._getUpdatedDraftEndpointDataForInvisibleComponents(inputDescriptor, this._draftAuth.parameters);
                        }

                        if (drawComponent) {
                            let inputKey = this._endpointInputDescriptorsKey + index;
                            this.authDetailsClass = "default-input-margin-top";

                            let inputComponent = null;
                            if (this._showConfigurationDropdown) {
                                inputComponent = EndpointsEditorPanelUtils.getComponent(inputKey, inputDescriptor, currentValue, (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                                    let inputValue = value;
                                    if (this._draftAuth.scheme === Constants.OAuth2Scheme) {
                                        this._draftAuth.parameters[Constants.AccessToken] = undefined;
                                        this._updateAuthInputsErrors(this._endpointNameErrorKey);
                                    }
                                    this._draftAuth.parameters[inputDescriptor.id] = inputValue;
                                    this._inputValuesDictionary[inputDescriptor.id] = inputValue;
                                    this._resetErrorDictionaryForDependentFieldsOfPreviousValue(inputDescriptor.id, currentValue);
                                    this._setDirtyFlag(inputValue, inputDescriptor.values, inputDescriptor.id);
                                    this._updateInputsErrors(inputValue, inputDescriptor.validation, inputDescriptor.id, inputDescriptor.isConfidential);
                                    this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                                },
                                    this._getErrorMessage(inputDescriptor.id),
                                    this.props.isUpdateForm,
                                    this.context.pageContext,
                                    this.configKey);

                                if (currentValue) {
                                    this._inputValuesDictionary[inputDescriptor.id] = currentValue;
                                } else {
                                    this._inputValuesDictionary[inputDescriptor.id] = EndpointsEditorPanelUtils.getComponentDefaultValue(inputDescriptor, inputComponent);
                                }
                                if (this._errorsDictionary[inputDescriptor.id] && inputDescriptor.values && inputDescriptor.values.isDisabled && inputDescriptor.validation.isRequired) {
                                    this._removeInputError(inputDescriptor.id);
                                }
                            }
                            return inputComponent ? <div className={this.authDetailsClass} key={inputKey}>{inputComponent}</div> : null;
                        }

                    })
                }
            </div>
        );
    }

     private _onAuthorizeClick(configurationId?: string) {
        this._isAuthDirty = true;
        this._isVerifying.value = true;
        this.createAuthRequest(configurationId);
    }

    private _getEndpointAuthenticationOAuth2Input(): JSX.Element {
        const isNewOauthUxCreate = this._isNewOAuthUx && this.props.oAuthProps.oAuthHelper.isCreateNewOAuthUx(this._draftAuth.scheme);
        if (this._draftAuth.parameters[Constants.AccessToken]
            && this._isNewOAuthUx
            && !isNewOauthUxCreate) {
            return;
        }

        return (
            <div className="authorize-oauth fontWeightSemiBold" key={"authorize-oauth"}>
                <Observer isVerifying={this._isVerifying}>
                    {(props: { isVerifying: boolean }) => {
                        if (props.isVerifying) {
                            return (
                                <Spinner ariaLive="assertive" label={Resources.SavingAuthorizationLabel} className="loading-spinner" size={SpinnerSize.medium} />
                            );
                        }
                        if (this._draftAuth.parameters[Constants.AccessToken] && !isNewOauthUxCreate) {
                            return (
                                <div>
                                    {this._draftServiceEndpoint.data.AvatarUrl && <img className="auth-user-pic" src={this._draftServiceEndpoint.data.AvatarUrl} alt="user pic" />}
                                    {this._draftServiceEndpoint.name}
                                    <Button
                                        tooltipProps={{ text: Resources.OauthChangeButtonDescription }}
                                        ariaLabel={Resources.OauthChangeButtonDescription}
                                        className="auth-button"
                                        text={Resources.OAuthChangeText}
                                        onClick={ () => { this._onAuthorizeClick(); } }
                                    />
                                </div>);
                        }
                        else {
                            return (
                                <FormItem
                                    message={this._isAuthDirty && this._errorsDictionary[this._endpointNameErrorKey]}
                                    error={this._isAuthDirty && !this._draftAuth.parameters[Constants.AccessToken]}
                                >
                                    {isNewOauthUxCreate
                                        ? this.props.oAuthProps.buttonComponent((configId: string) => this._onAuthorizeClick(configId))
                                        : <ButtonGroup>
                                            <Button
                                                ariaLabel={Resources.OAuthAuthorizeText}
                                                text={Resources.OAuthAuthorizeText}
                                                disabled={!this._draftAuth.parameters[Constants.ConfigurationId] && !this.props.isUpdateForm && this._showConfigurationDropdown}
                                                primary={true}
                                                onClick={ () => { this._onAuthorizeClick(); } }
                                            />
                                        </ButtonGroup>
                                    }

                                </FormItem>);
                        }
                    }}
                </Observer>
            </div>
        );
    }

    private _initializeConfigurationsIfRequired(serviceEndpointType: ServiceEndpointType, hasAuthSchemeChanged: boolean): void {
        let authenticationScheme: ServiceEndpointAuthenticationScheme;
        let selectedScheme = this._draftAuth.scheme;
        if (this._draftAuth.scheme && this._draftAuth.scheme === Constants.OAuth2Scheme) {
            authenticationScheme = serviceEndpointType.authenticationSchemes.filter(x => Utils_String.equals(x.scheme, selectedScheme, true))[0];
            this._initializeOAuth2Inputs();
            //Validate endpoint's OAuth2 auth scheme
            this._updateAuthInputsErrors(this._endpointNameErrorKey);
        }
        if (authenticationScheme
            && Utils_String.equals(authenticationScheme.scheme, Constants.OAuth2Scheme, true)
            && authenticationScheme.requiresOAuth2Configuration !== undefined
            && !authenticationScheme.requiresOAuth2Configuration) {
            this._showConfigurationDropdown = false;
            this._authUrl = authenticationScheme.authorizationUrl;
            // We are doing this explicitly because the two invalid inputs, i.e. endpointUrl & configId, are not needed in public client scenario.
            // Hence, removing this here. The token input and endpointName input will be handled as usual.
            if (this.props.isUpdateForm) {
                if (!this._draftServiceEndpoint.url) {
                    this._totalInputErrors--;
                    this.props.updateInvalidInputsCount(-1);
                }
                if (hasAuthSchemeChanged) {
                    this._totalInputErrors--;
                    this.props.updateInvalidInputsCount(-1);
                }
            }
            else {
                if (this._draftServiceEndpoint.url) {
                    this._totalInputErrors--;
                    this.props.updateInvalidInputsCount(-1);
                }
                else {
                    this._totalInputErrors = this._totalInputErrors - 2;
                    this.props.updateInvalidInputsCount(-2);
                }
            }
        }
        else {
            this._showConfigurationDropdown = true;
        }
    }

    private getAuthRequest(serviceEndpoint: ServiceEndpoint): Contracts.AuthRequest {
        let properties = { "acceptUntrustedCerts": serviceEndpoint.data["acceptUntrustedCerts"] };
        let authRequest: Contracts.AuthRequest = {
            url: "",
            errorMessage: "",
            properties: properties
        };

        if (!this._showConfigurationDropdown) {
            authRequest.url = this._authUrl;
        }
        return authRequest;
    }

    private createAuthRequest(configurationId?: string) {
        let provider = Constants.OAuth2Scheme.toLowerCase();
        let authRequest: Contracts.AuthRequest = this.getAuthRequest(this._draftServiceEndpoint);
        let username: string = "";

        this._handleOAuthEndpoint(configurationId);
        this.props.serviceEndpointType.authenticationSchemes.map((authenticationScheme) => {
            if (Utils_String.equals(authenticationScheme.scheme, Constants.OAuth2Scheme, true)) {
                if (authenticationScheme.properties && authenticationScheme.properties[Constants.Provider]) {
                    provider = authenticationScheme.properties[Constants.Provider];
                }
                username = this._getEndpointNamePrefix();
            }
        });


        if (!this._draftAuth.parameters[Constants.ConfigurationId]) {
            this._draftAuth.parameters[Constants.ConfigurationId] = "00000000-0000-0000-0000-000000000000";
        }

        // We are not using createAuthRequest result, rely on side effect
        this._endPointControls.createAuthRequest(this._draftServiceEndpoint, username, provider, this._draftAuth.parameters[Constants.ConfigurationId], authRequest).then((endpoint: ServiceEndpoint) => {
            this._updateAuthInputsErrors(this._endpointNameErrorKey);
            if (this._isNewOAuthUx && this.props.oAuthProps.oAuthHelper.isCreateNewOAuthUx(this._draftAuth.scheme)) {
                this.props.oAuthProps.oAuthCreatedAndAuthorizedCallback(this._draftAuth.parameters[Constants.ConfigurationId]);
            }
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);

        }, (error) => {
            this._updateAuthInputsErrors(this._endpointNameErrorKey);
            if (this._isNewOAuthUx && this.props.oAuthProps.oAuthHelper.isCreateNewOAuthUx(this._draftAuth.scheme)) {
                this.props.oAuthProps.oAuthCreatedAndAuthorizedCallback(
                    configurationId,
                    Resources.OAuthAuthFailed,
                    true);
            }
        }
        );
    }

    private uploadDialog(): JSX.Element {
        let _resultContentType = Constants.SSHResultContentType;
        return (
            <FormItem
                className="ssh-endpoint-private-key-box"
                    >
                <Observer showDialog={this.state.showUploadDialog}>
                    {
                        (props: { showDialog: boolean }) => {
                            if (props.showDialog) {
                                return (
                                    <Dialog
                                        titleProps={{ text: Resources.SSHUploadFile }}
                                        footerButtonProps={[
                                            {
                                                text: Resources.OK,
                                                onClick: () => { this.onOk(); },
                                                disabled: !this.state.fileContent
                                            },
                                            {
                                                text: Resources.Cancel,
                                                onClick: () => { this.onDismiss(); }
                                            }
                                        ]}
                                        onDismiss={
                                            () => {
                                                this.onDismiss();
                                            }
                                        }>
                                        <FileInput maximumNumberOfFiles={Number(Constants.SSHmaximumNumberOfFiles)}
                                            maximumSingleFileSize={Number(Constants.SSHMaximumSingleFileSize)}
                                            updateHandler={this._onFileInputUpdate}
                                            resultContentType={_resultContentType}
                                        >
                                        </FileInput>
                                    </Dialog>
                                );
                            }
                            return null;
                        }
                    }
                </Observer>
            </FormItem >
        );
    }

    private onUploadClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this.setState({ showUploadDialog: true });
    }

    private _onFileInputUpdate = (updateEvent: FileInputUpdateEventData): void => {
        let file: FileInputResult = null;
        this.onFileUpdate = "upload file";
        if (updateEvent && updateEvent.files && updateEvent.files.length) {
            file = updateEvent.files[0].result;
            this.isFileUploaded = true;
        }
        else {
            this.isFileUploaded = false;
        }
        this.setState({ fileContent: file });
    }

    private onDismiss() {
        this.setState({ showUploadDialog: false, fileContent: null });
    }

    private onOk() {
        this._draftServiceEndpoint.data[Resources.SSHPrivateKey] = this.state.fileContent.content.toString();
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.setState({ showUploadDialog: false, fileContent: null });
    }

    private _getUpdatedDraftEndpointDataForInvisibleComponents = (inputDescriptor: InputDescriptor, draftEndpointParams: {[key: string] : string}): {[key: string] : string} => {
        const newDraftEndpointParams = Object.keys(draftEndpointParams).reduce((object: {[key: string] : string; }, key: string) => {
            if (key !== inputDescriptor.id) {
                object[key] = draftEndpointParams[key];
            }
            return object;
            }, {});
        return newDraftEndpointParams;
    }

    private _updateUrlIfApplicable = (inputDescriptorId: string, key: string) => {
        const urlDependsOnField =  this.props.serviceEndpointType.endpointUrl && this.props.serviceEndpointType.endpointUrl.dependsOn;
        if (urlDependsOnField && urlDependsOnField.input && Utils_String.equals(urlDependsOnField.input, inputDescriptorId, true)) {
            urlDependsOnField.map.forEach((object, index) => {
                if (Utils_String.equals(object.key, key, true)) {
                    if (object.value) {
                        this._draftServiceEndpoint.url = object.value;
                        if (this._errorsDictionary[this._endpointUrlKey]) {
                            this._removeInputError(this._endpointUrlKey);
                        }
                    } else {
                        this._draftServiceEndpoint.url = "";
                        if (!this._errorsDictionary[this._endpointUrlKey]) {
                            this._addInputError(this._endpointUrlKey, this._urlInputValidation);
                        }
                    }
                }
            });
        }
    }

    private _resetErrorDictionaryForDependentFieldsOfPreviousValue = (inputDescriptorId: string, prevValue: string) => {
        const dependentFields: [] = this._dependencyDictionary[inputDescriptorId];
        if (dependentFields && dependentFields.length > 0) {
            dependentFields.forEach(dependentField => {
                const inputDescriptorForDependentField = this._inputDescriptorsDictionary[dependentField];
                if (inputDescriptorForDependentField && inputDescriptorForDependentField.properties && inputDescriptorForDependentField.validation && inputDescriptorForDependentField.validation["isRequired"]) {
                    const isVisible: boolean = VisibilityHelper.getVisibility(inputDescriptorForDependentField.properties[Constants.VisibleRule], this._getInputValue);
                    if (!isVisible) {
                        if (this._errorsDictionary[dependentField]) {
                            this._removeInputError(dependentField);
                        }
                    } else {
                        // update errors only if existing inputs for dependent fields are invalid
                        if (!EndpointsEditorPanelUtils.isInputValid(this._draftServiceEndpoint.data[dependentField], inputDescriptorForDependentField.validation, isVisible)
                            && this._draftAuth && this._draftAuth.parameters && !EndpointsEditorPanelUtils.isInputValid(this._draftAuth.parameters[dependentField], inputDescriptorForDependentField.validation, isVisible)) {
                            if (!this._errorsDictionary[dependentField]) {
                                // since we dont validate confidential parameters in update flow
                                if (!(this.props.isUpdateForm && inputDescriptorForDependentField.isConfidential)) {
                                    this._addInputError(dependentField, inputDescriptorForDependentField.validation);
                                }
                            }
                        }
                    }
                }
            });
        }
    }

    private _getInputValue = (inputName: string): any => {
        if (this._inputValuesDictionary[inputName] !== undefined) {
            // stringify value so that input components like checkbox can process the value correctly
            return this._inputValuesDictionary[inputName] + "";
        }

        return "";
    }

    private _getInputValueForAuthInputsFromDraftEndpoint = (inputName: string): any => {
        if (this._draftAuth && this._draftAuth.parameters && this._draftAuth.parameters[inputName] !== undefined) {
            return this._draftAuth.parameters[inputName] + "";
        }

        return "";
    }

    private _getInputValueForNonAuthInputsFromDraftEndpoint = (inputName: string): any => {
        if (this._draftServiceEndpoint.data && this._draftServiceEndpoint.data[inputName] !== undefined) {
            return this._draftServiceEndpoint.data[inputName] + "";
        }

        return "";
    }

    private _getEndpointNamePrefix() {
        if (Utils_String.equals(this._draftServiceEndpoint.type, ServiceEndpointTypes.GitHub, true)) {
            return Constants.GitHubUserNamePrefix;
        }

        if (Utils_String.equals(this._draftServiceEndpoint.type, ServiceEndpointTypes.GitHubEnterprise, true)) {
            const url = this._draftServiceEndpoint.url;
            const prefix = url ? new URL(url).host : ServiceEndpointTypes.GitHubEnterprise;
            return prefix + "_";
        }

        return "";
    }

    private _getSelectedAuthSchemeIndex(): number {
        if (!this.props.serviceEndpointType) {
            return 0;
        }

        let index = this.props.serviceEndpointType.authenticationSchemes.findIndex((authScheme) => {
            return authScheme.scheme === this._draftAuth.scheme;
        });

        return index >= 0 ? index : 0;
    }

    private _initializeInputErrors() {
        // Clear existing error count
        this.props.updateInvalidInputsCount(this._totalInputErrors * -1);
        this._totalInputErrors = 0;
        this._errorsDictionary = {};

        // Validate endpoint's auth inputs
        if (this.props.serviceEndpointType && this.props.serviceEndpointType.authenticationSchemes) {
            this.props.serviceEndpointType.authenticationSchemes[this._getSelectedAuthSchemeIndex()].inputDescriptors.map((inputDescriptor, index) => {
                if (!this._inputDescriptorsDictionary[inputDescriptor.id]) {
                    this._inputDescriptorsDictionary[inputDescriptor.id] = inputDescriptor;
                }
                let currentValue = this._draftAuth.parameters[inputDescriptor.id];
                let isVisible = undefined;
                if (inputDescriptor.properties && inputDescriptor.properties[Constants.VisibleRule]) {
                    isVisible = VisibilityHelper.getVisibility(inputDescriptor.properties[Constants.VisibleRule], this._getInputValueForAuthInputsFromDraftEndpoint);
                }
                this._updateInputsErrors(currentValue, inputDescriptor.validation, inputDescriptor.id, inputDescriptor.isConfidential, isVisible);
            });
        }

        // Validate endpoint's non auth inputs
        if (this.props.serviceEndpointType && this.props.serviceEndpointType.inputDescriptors) {
            this.props.serviceEndpointType.inputDescriptors.map((inputDescriptor, index) => {
                if (!this._inputDescriptorsDictionary[inputDescriptor.id]) {
                    this._inputDescriptorsDictionary[inputDescriptor.id] = inputDescriptor;
                }
                let currentValue = this._draftServiceEndpoint.data[inputDescriptor.id];
                let isVisible = undefined;
                if (inputDescriptor.properties && inputDescriptor.properties[Constants.VisibleRule]) {
                    isVisible = VisibilityHelper.getVisibility(inputDescriptor.properties[Constants.VisibleRule], this._getInputValueForNonAuthInputsFromDraftEndpoint);
                }
                this._updateInputsErrors(currentValue, inputDescriptor.validation, inputDescriptor.id, inputDescriptor.isConfidential, isVisible);
            });
        }
    }

    private _initializeDependencyMap() {
        this.props.serviceEndpointType.inputDescriptors.forEach((inputDescriptor) => {
            this._addDependentFieldsToDictionary(inputDescriptor);
        });

        const selectedAuthSchemeIndex = this._getSelectedAuthSchemeIndex();
        if (this.props.serviceEndpointType.authenticationSchemes && this.props.serviceEndpointType.authenticationSchemes[selectedAuthSchemeIndex] && this.props.serviceEndpointType.authenticationSchemes[selectedAuthSchemeIndex].inputDescriptors) {
            this.props.serviceEndpointType.authenticationSchemes[selectedAuthSchemeIndex].inputDescriptors.map((inputDescriptor) => {
                this._addDependentFieldsToDictionary(inputDescriptor);
            });
        }
    }

    private _addDependentFieldsToDictionary(inputDescriptor: InputDescriptor) {
        if (inputDescriptor.properties && inputDescriptor.properties[Constants.VisibleRule]) {
            const visibleRule = VisibilityHelper.getVisibilityRule(inputDescriptor.properties[Constants.VisibleRule]);
            visibleRule.predicateRules.forEach(predicateRule => {
                if (!this._dependencyDictionary[predicateRule.inputName]) {
                    this._dependencyDictionary[predicateRule.inputName] = [];
                }
                let dependentFieldsList = this._dependencyDictionary[predicateRule.inputName];
                dependentFieldsList.push(inputDescriptor.id);
                this._dependencyDictionary[predicateRule.inputName] = dependentFieldsList;
            });
        }
    }

    private _updateInputsErrors = (value: any, inputValidation: InputValidation, inputId: string, isConfidential: boolean, isVisible?: boolean) => {
        if (this.props.isUpdateForm && isConfidential) {
            return;
        }

        const isInputValid = isVisible === undefined ? EndpointsEditorPanelUtils.isInputValid(value, inputValidation) : EndpointsEditorPanelUtils.isInputValid(value, inputValidation, isVisible);
        const isInputPresent = !(value === undefined || value === "");
        const inputDescriptor = this._inputDescriptorsDictionary[inputId];

        if (this.props.onValidationChange) {
            this.props.onValidationChange(inputDescriptor, isInputValid, isInputPresent);
        }

        if (!isInputValid) {
            if (!this._errorsDictionary[inputId]) {
                // since we dont validate confidential parameters in update flow
                if (!(this.props.isUpdateForm && isConfidential)) {
                    this._addInputError(inputId, inputValidation);
                }
            }
        } else {
            if (this._errorsDictionary[inputId]) {
                this._removeInputError(inputId);
            }
        }
    }

    private _updateAuthInputsErrors(inputId: string) {
        this._isVerifying.value = false;
        if (!this._draftAuth.parameters[Constants.AccessToken]) {
            if (!this._errorsDictionary[inputId]) {
                this._totalInputErrors++;
                this.props.updateInvalidInputsCount(1);
            }
            this._errorsDictionary[inputId] = Resources.CouldNotCompleteOAuth;
        }
        else {
            if (this._errorsDictionary[inputId]) {
                this._removeInputError(inputId);
            }
        }
    }

    private _removeInputError = (key: string) => {
        this._errorsDictionary[key] = "";
        this._totalInputErrors--;
        this.props.updateInvalidInputsCount(-1);
    }

    private _addInputError = (key: string, validation: InputValidation) => {
        this._errorsDictionary[key] = EndpointsEditorPanelUtils.getInvalidInputErrorMessage(validation);
        this._totalInputErrors++;
        this.props.updateInvalidInputsCount(1);
    }

    private _initializeOAuth2Inputs() {
        this._endPointControls = new EndpointOAuthHelper.EndpointOAuthHelper(this.context.pageContext);
        this._isVerifying = new ObservableValue(false);
        this._isAuthDirty = false;
    }

    private shouldShowUrl(serviceEndpointType: ServiceEndpointType): boolean {
        if (!serviceEndpointType || !serviceEndpointType.endpointUrl) {
            return true;
        }

        if (this.props.serviceEndpointType.endpointUrl && Utils_String.equals(this.props.serviceEndpointType.endpointUrl.isVisible, "false", true)) {
            return false;
        }

        let shouldShowUrl: boolean = true;
        // overriding the shouldShowUrl behavior incase of create flow- i.e not showing url if we already have it specified in the json
        if (!this.props.isUpdateForm && serviceEndpointType.endpointUrl && serviceEndpointType.endpointUrl.dependsOn && serviceEndpointType.endpointUrl.dependsOn.map) {
            serviceEndpointType.endpointUrl.dependsOn.map.forEach((dependencyBinding: DependencyBinding, index: number) => {
                if (Utils_String.equals(this._draftServiceEndpoint.data.environment, dependencyBinding.key, true) && dependencyBinding.value) {
                    shouldShowUrl = false;
                }
            });
        }

        return shouldShowUrl;
    }

    private _setInputDescriptorWithUrlDependency = () => {
        if (this.props.serviceEndpointType.endpointUrl && this.props.serviceEndpointType.endpointUrl.dependsOn && this.props.serviceEndpointType.endpointUrl.dependsOn.input) {
            this._inputDescriptorIdWithUrlDependency = this.props.serviceEndpointType.endpointUrl.dependsOn.input;
        }
    }

    private _setDirtyFlag(currentValue: any, initialValue: InputValues, inputId: string) {
        let isDirty = false;
        if (initialValue && initialValue.defaultValue) {
            isDirty = currentValue !== initialValue.defaultValue;
        } else {
            isDirty = true;
        }

        this._isDirtyDictionary[inputId] = isDirty;
    }

    private _isErrorVisible(inputId: string): boolean {
        return (this._isDirtyDictionary[inputId] && !!this._errorsDictionary[inputId]);
    }

    private _getErrorMessage(inputId: string): string {
        return this._isErrorVisible(inputId) ? this._errorsDictionary[inputId] : "";
    }

    private _onEndpointEditorStoreChange = () => {
        this.setState(this._endpointEditorStore.getState());
    }

    private _handleOAuthEndpoint(configurationId?: string) {
        if (this._isNewOAuthUx && this.props.oAuthProps.oAuthHelper.isUseExistingOAuthUx(this._draftAuth.scheme)) {
            if (this._draftAuth.parameters[Constants.ConfigurationId]) {
                const config = this.state.configurations.find((config) => config.id === this._draftAuth.parameters[Constants.ConfigurationId]);
                if (config && this._draftServiceEndpoint.url !== config.url) {
                    this._draftServiceEndpoint.url = config.url;
                }
            }
        } else if (this._isNewOAuthUx && this.props.oAuthProps.oAuthHelper.isCreateNewOAuthUx(this._draftAuth.scheme) && configurationId) {
            this._draftServiceEndpoint.url = this.props.oAuthProps.createdOAuthUrl;
            if (!this._draftAuth.parameters[Constants.ConfigurationId]) {
                this._draftAuth.parameters[Constants.ConfigurationId] = configurationId;
            }
        }
    }

    private oAuthRender() {
        return (
            <div className="auth-scheme-details-section">
                {
                    this._getEndpointAuthenticationInputs()
                }
                {
                    this.props.serviceEndpointType && this.props.serviceEndpointType.inputDescriptors && this._getEndpointDetailsInputs()
                }
                {
                    this._getEndpointAuthenticationOAuth2Input()
                }
            </div>
        );
    }

    private _isNewOAuthUx: boolean;
    private _draftServiceEndpoint: ServiceEndpoint;
    private _draftAuth: EndpointAuthorization;
    private _errorsDictionary: { [key: string]: string };
    private _isDirtyDictionary: { [key: string]: boolean };
    private _endpointUrlKey = "endpoint-url";
    private _endpointInputDescriptorsKey = "endpoint-input-descriptor-";
    private _urlInputValidation: InputValidation = {
        dataType: InputDataType.Uri,
        isRequired: true,
        maxLength: 200,
        maxValue: null,
        minLength: null,
        minValue: null,
        pattern: "",
        patternMismatchErrorMessage: ""
    };
    private _inputValuesDictionary: { [key: string]: any } = {};
    private _isErrorDictInitialized = false;
    private _selectedAuthScheme = "";
    private _totalInputErrors = 0;
    private authDetailsClass: string;
    private _isVerifying: ObservableValue<boolean>;
    private _isAuthDirty: boolean;
    private _endPointControls: EndpointOAuthHelper.EndpointOAuthHelper;
    private _endpointNameErrorKey = "oauth-params";
    private _showConfigurationDropdown: boolean = true;
    private _authUrl: string;
    private _endpointEditorStore: EndpointsEditorStore;
    private _dependencyDictionary: { [key: string]: any } = {};
    private _inputDescriptorsDictionary: { [key: string]: InputDescriptor } = {};
    private _inputDescriptorIdWithUrlDependency: string;
    private configKey: DropdownSelection = null;
    private isFileUploaded: boolean;
    private onFileUpdate: string;
}
